/* eslint-disable no-useless-escape */
import { KeyboardArrowDownRounded } from '@mui/icons-material';
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAsyncAction, useStateRef } from 'app/services';
import { issueTypeFormLoad } from 'app/store/issue';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { useSelector } from 'react-redux';

const IssueForm = forwardRef((props, ref) => {
  const filesRef = useRef(null);
  const theme = useTheme();
  const [firstUpdate, setFirstUpdate, firstUpdateRef] = useStateRef(false);

  const { asyncAction: fetchData, isLoading: isFetchLoading } = useAsyncAction(issueTypeFormLoad);
  const { typeItems } = useSelector((state) => state.issue);
  const { onSubmit } = props;
  const { control, register, formState, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      title: '',
      type_id: '',
      name: '',
      number: '',
      trailer_number: '',
      description: '',
      attachments: [],
    },
  });

  const typeId = watch('type_id');
  const attachmentsValue = watch('attachments');

  useImperativeHandle(ref, () => ({
    handleFormSubmit: () => {
      handleSubmit((data) =>
        onSubmit({
          title: data.title,
          type_id: parseInt(data.type_id),
          name: data.name ? data.name : undefined,
          number: data.number ? data.number : undefined,
          trailer_number: data.trailer_number ? data.trailer_number : undefined,
          description: data.description,
          attachments: data.attachments ? data.attachments : [],
        })
      )();
    },
  }));

  const handleInputFileChange = (e) => {
    const reader = new FileReader();

    reader.readAsDataURL(e.target.files[0]);

    reader.onerror = () => setValue('attachments', []);
    reader.onload = () =>
      setValue('attachments', [
        {
          ext: '.' + e.target.files[0].type.split('/')[1],
          name: e.target.files[0].name,
          type: e.target.files[0].type,
          base64: reader.result.split('base64,')[1],
        },
      ]);
  };

  useEffect(() => {
    if (firstUpdateRef.current !== undefined) {
      setTimeout(() => setFirstUpdate(undefined), theme.transitions.duration.shorter);
    }
  }, [firstUpdateRef.current]);

  useEffect(() => {
    if (typeId) {
      setValue('title', typeItems.find((issue) => issue.id === typeId)?.title);
    }
  }, [typeId]);

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <form>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl error={!!formState.errors.type_id} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Тема проблемы</InputLabel>
            {isFetchLoading && (
              <OutlinedInput
                disabled
                endAdornment={
                  <InputAdornment position='end'>
                    <CircularProgress size={16} />
                  </InputAdornment>
                }
              />
            )}
            {!isFetchLoading && (
              <Controller
                name='type_id'
                control={control}
                render={({ field }) => (
                  <Select
                    label='Тема проблемы'
                    IconComponent={KeyboardArrowDownRounded}
                    notched={firstUpdate}
                    value={field.value}
                    onChange={field.onChange}
                    MenuProps={{
                      sx: {
                        '.MuiPaper-root': {
                          maxHeight: 200,
                          overflow: 'auto',
                          marginTop: '1px',
                        },
                        '.MuiPaper-root:before': {
                          display: 'none',
                        },
                      },
                    }}
                    {...register(field.name, {
                      pattern: /^[A-Za-zА-Яа-я0-9_\W\s]+$/iu,
                      required: true,
                    })}>
                    {typeItems.map((value, key) => (
                      <MenuItem key={key} disableRipple value={value.id}>
                        {value.title}
                      </MenuItem>
                    ))}
                  </Select>
                )}></Controller>
            )}
            <FormHelperText>Выберите тему вопроса</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.number} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Госномер транспортного средства</InputLabel>
            <Controller
              name='number'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='A999AA 999'
                  maskChar=''
                  value={field.value}
                  onChange={field.onChange}
                  formatChars={{
                    9: '[0-9]',
                    A: '[АВЕКМНОРСТУХ]',
                  }}
                  {...register(field.name, {
                    pattern: /^([АВЕКМНОРСТУХ]{1}[0-9]{3}[АВЕКМНОРСТУХ]{2}\s[0-9]{2,3})?$/iu,
                    required: true,
                  })}
                  {...field}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Госномер транспортного средства'
                      variant='outlined'
                      notched={firstUpdate}
                      fullWidth
                      placeholder='А000АА 77'
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
            <FormHelperText>Используйте регистрационный номер в указанном формате А000АА 77</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.trailer_number} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Госномер прицепа</InputLabel>
            <Controller
              name='trailer_number'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='AА9999 999'
                  maskChar=''
                  value={field.value}
                  onChange={field.onChange}
                  formatChars={{
                    9: '[0-9]',
                    A: '[АВЕКМНОРСТУХ]',
                  }}
                  {...register(field.name, {
                    pattern:
                      /^([АВЕКМНОРСТУХ]{2}\d{4}\s\d{2}|[АВЕКМНОРСТУХ]{1}\d{3}[АВЕКМНОРСТУХ]{2}\s\d{2}|[АВЕКМНОРСТУХ]{1}\d{3}[АВЕКМНОРСТУХ]{2}\s\d{3}|[A-Z]{1}\d{4}[A-Z]\-\d{1}|[A-Z]{2}\s\d{4}\-\d{1}|\d{4}\s[A-Z]{2}\-\d{1}|\d{2}[A-Z]{2}\d{3}|\d{2}\-[A-Z]{2}\-\d{3})$/,
                  })}
                  {...field}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Госномер прицепа'
                      variant='outlined'
                      notched={firstUpdate}
                      fullWidth
                      placeholder='АА0000 77'
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
            <FormHelperText>Используйте регистрационный номер в указанном формате АА0000 77</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl error={!!formState.errors.description} margin='none' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Описание проблемы</InputLabel>
            <OutlinedInput
              variant='outlined'
              label='Описание проблемы'
              notched={firstUpdate}
              multiline
              fullWidth
              minRows={7}
              sx={{ padding: 0 }}
              {...register('description', {
                pattern: /^[A-Za-zА-Яа-я0-9_\-\W\s]+$/iu,
                required: true,
              })}></OutlinedInput>
            <FormHelperText>Опишите проблему или задайте Ваш вопрос</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl
            error={!!formState.errors.files}
            margin='none'
            variant='outlined'
            fullWidth
            sx={{
              marginTop: '12px',
              label: {
                display: 'flex',
              },
              '.MuiButton-root': {
                width: '40%',
                marginLeft: '12px',
              },
            }}>
            <label ref={filesRef} htmlFor='upload-photo'>
              <input
                id='upload-photo'
                name='upload-photo'
                type='file'
                accept='image/png, image/jpeg'
                style={{ display: 'none' }}
                onChange={handleInputFileChange}
              />
              <OutlinedInput
                variant='outlined'
                notched
                placeholder='Выберите файл'
                readOnly
                onClick={() => filesRef.current.click()}
                value={attachmentsValue.length ? attachmentsValue[0].name : ''}
                fullWidth
              />
            </label>
            <FormHelperText>Загрузите файл расширением .png, .jpeg не превышающим лимит 50Mb</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
    </form>
  );
});

export default IssueForm;
